import { useEffect, useRef, useState } from "react";
import { X, Send, Sparkles, ExternalLink, RotateCcw, Phone } from "lucide-react";
import { API } from "../lib/api";

const SseReader = async (response, onToken, onDone, onError) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      try {
        const evt = JSON.parse(line.slice(5));
        if (evt.t === "token") onToken(evt.d);
        else if (evt.t === "done") onDone(evt);
        else if (evt.t === "error") onError(evt.d);
      } catch { /* partial chunk */ }
    }
  }
};

export const Concierge = ({ open, onClose, suggested = [], onOpenPanel, phoneDisplay, seed }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 250);
  }, [open]);

  const seededRef = useRef(false);
  useEffect(() => {
    if (open && seed && !seededRef.current) {
      seededRef.current = true;
      send(seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seed]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  if (!open) return null;

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    const history = messages.filter((m) => !m.error).map((m) => ({ role: m.role, text: m.text }));
    const guestMsg = { role: "guest", text: message };
    setMessages((prev) => [...prev, guestMsg]);
    setBusy(true);
    try {
      const resp = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, history }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "The concierge couldn't answer just now.");
      }
      let streamed = "";
      const guideMsg = { role: "guide", text: "", links: [], sources: [], live: false };
      setMessages((prev) => [...prev, guideMsg]);
      await SseReader(
        resp,
        (d) => {
          streamed += d;
          setMessages((prev) => prev.map((m, i) => (i === prev.length - 1 ? { ...m, text: streamed } : m)));
        },
        (evt) => {
          setMessages((prev) =>
            prev.map((m, i) => (i === prev.length - 1 ? { ...m, text: evt.text, links: evt.links, sources: evt.sources, live: evt.live } : m))
          );
        },
        (d) => {
          setMessages((prev) => prev.map((m, i) => (i === prev.length - 1 ? { role: "guide", text: d, error: true } : m)));
        }
      );
    } catch (e) {
      setMessages((prev) => [...prev, { role: "guide", text: e.message, error: true }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label="Pebble Farms Concierge" data-testid="concierge-dialog">
      <button className="absolute inset-0 bg-charcoal/60 backdrop-blur-sm" onClick={onClose} aria-label="Close concierge" data-testid="concierge-backdrop" />
      <div className="relative w-full sm:max-w-lg bg-cream rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[88vh] overflow-hidden border border-charcoal/10">
        <div className="flex items-center justify-between px-5 py-4 bg-charcoal text-cream">
          <div className="flex items-center gap-2.5">
            <Sparkles size={18} className="text-copper" aria-hidden />
            <div>
              <p className="font-serif font-semibold leading-none">Pebble Farms Concierge</p>
              <p className="text-xs text-cream/70 mt-1">Answers from the Pebble Farms guest guide</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-cream/10 transition-colors" aria-label="Close" data-testid="concierge-close-button">
            <X size={20} />
          </button>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-[280px]">
          {messages.length === 0 && (
            <div>
              <p className="text-soft text-base mb-4">Ask me anything about the house, the amenities, or the area.</p>
              <div className="flex flex-wrap gap-2">
                {suggested.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    className="text-left text-sm bg-white border border-charcoal/15 rounded-full px-4 py-2.5 hover:border-copper hover:text-copperdeep transition-colors"
                    data-testid={`concierge-suggestion-${q.slice(0, 18).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "guest" ? "flex justify-end" : "flex justify-start"}>
              <div className={m.role === "guest"
                ? "bg-copper text-cream rounded-2xl rounded-br-md px-4 py-3 max-w-[85%] text-base"
                : `rounded-2xl rounded-bl-md px-4 py-3 max-w-[88%] text-base ${m.error ? "bg-red-50 border border-red-200 text-red-900" : "bg-white border border-charcoal/10 text-charcoal"}`}>
                <p className="whitespace-pre-wrap leading-relaxed">{m.text || (busy && i === messages.length - 1 ? "…" : "")}</p>
                {m.live && <p className="text-xs mt-2 text-sagedeep font-semibold">Includes live web results — details can change.</p>}
                {m.sources?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {m.sources.map((s) => (
                      <a key={s.url} href={s.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-copperdeep underline underline-offset-2">
                        <ExternalLink size={12} aria-hidden /> {s.title || s.url}
                      </a>
                    ))}
                  </div>
                )}
                {m.links?.length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {m.links.map((id) => (
                      <button key={id} onClick={() => { onClose(); onOpenPanel(id); }}
                        className="text-xs font-semibold bg-sage/15 text-sagedeep border border-sage/30 rounded-full px-3 py-1.5 hover:bg-sage/25 transition-colors"
                        data-testid={`concierge-guide-link-${id}`}>
                        Open in guide
                      </button>
                    ))}
                  </div>
                )}
                {m.error && (
                  <div className="mt-3 flex items-center gap-2">
                    <button onClick={() => { setMessages((prev) => prev.slice(0, -1)); send(messages.filter((x) => x.role === "guest").slice(-1)[0]?.text); }}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold bg-copper text-cream rounded-full px-3 py-1.5"
                      data-testid="concierge-retry-button">
                      <RotateCcw size={12} aria-hidden /> Try again
                    </button>
                    <a href="tel:+12102743556" className="inline-flex items-center gap-1.5 text-xs font-semibold underline underline-offset-2" data-testid="concierge-call-host-link">
                      <Phone size={12} aria-hidden /> {phoneDisplay}
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && messages[messages.length - 1]?.role === "guest" && (
            <div className="flex justify-start">
              <div className="bg-white border border-charcoal/10 rounded-2xl rounded-bl-md px-4 py-3" data-testid="concierge-loading">
                <span className="inline-flex gap-1" aria-label="Concierge is typing">
                  <span className="w-2 h-2 rounded-full bg-copper animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 rounded-full bg-copper animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="w-2 h-2 rounded-full bg-copper animate-bounce" style={{ animationDelay: "240ms" }} />
                </span>
              </div>
            </div>
          )}
        </div>

        <form className="flex items-center gap-2 px-4 py-3 border-t border-charcoal/10 bg-white"
          onSubmit={(e) => { e.preventDefault(); send(); }}>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            maxLength={500}
            placeholder="Ask about the pool, Wi-Fi, dinner spots…"
            className="flex-1 bg-cream rounded-full px-4 py-3 text-base outline-none border border-transparent focus:border-copper"
            aria-label="Message the concierge"
            data-testid="concierge-input"
          />
          <button type="submit" disabled={busy || !input.trim()}
            className="p-3 rounded-full bg-copper text-cream disabled:opacity-40 hover:bg-copperdeep transition-colors"
            aria-label="Send message" data-testid="concierge-send-button">
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
};
